/**
 * Шумоподавление: узел ворклета "noise-cancellation-processor" и опциональный
 * движок (web worker с onnxruntime-web) к главному потоку. Оба артефакта
 * отдаются статикой из public/noise. Модуль владеет ими, выполняет хендшейк
 * портов и отдаёт наружу узел для врезки в аудиограф:
 *
 *   sourceNode.connect(nc.node);
 *   nc.node.connect(destinationNode);
 *
 * Узел моно (1 вход → 1 выход), вход в моно сводит сам ворклет.
 *
 * Узел задуман как ПОСТОЯННАЯ часть микрофонного графа: он всегда умеет две
 * вещи независимо от движка — (1) глушить выход по {type:"track_mute_change"}
 * (это и есть mute процессора, без которого правило «трек мутится и через
 * трек, и через процессор» не выполнимо) и (2) пропускать вход как есть
 * (passthrough), пока движок не готов. Поэтому узел можно врезать в граф ДО
 * разрешения `ready`: пока модель грузится, ворклет пишет в выход сырой вход,
 * паузы или тишины не возникает. После готовности аудио идёт со штатной
 * задержкой latency + lookahead + workletFrameSize = 1536 сэмплов (~32 мс
 * @48кГц).
 *
 * Движок опционален: без `workerUrl` воркер не создаётся вообще, `ready` уже
 * разрешён, а узел навсегда остаётся passthrough и подчиняется mute с первого
 * же кванта.
 *
 * Выход готового узла переключается режимом: { type: "set_mode", mode } меняет
 * его на ходу, ничего не пересоздавая. "bypass" перестаёт отправлять кадры
 * движку и отдаёт сырой вход, выровненный на тот же таймлайн, а "denoise"
 * возвращает аудио движку штатной coverage-driven цепочкой; кольца, таймлайн и
 * уже загруженную модель режим не трогает. Тихий отказ движка без событий
 * тишины не даёт: окна, не закрытые результатами, контроллер добирает из сырого
 * входа. Тишина — и то навсегда — только там, где узел рвём мы сами (destroy
 * или фатал): после этого объект непригоден, нужен новый
 * createNoiseCancellation.
 *
 * Протокол (не менять):
 *   - ПЕРВОЕ сообщение воркера — переданный MessagePort; его нужно немедленно
 *     отдать ворклету первым сообщением node.port;
 *   - { type: "loading_done" } из воркера ретранслируется ворклету (снимает
 *     passthrough) и означает готовность пайплайна;
 *   - { type: "statistics", inferenceMs } игнорируется: телеметрии в проекте нет;
 *   - всё остальное из воркера и worker.onerror — фатально;
 *   - главный поток → ворклет: loading_done, track_mute_change, set_mode,
 *     destroy (см. WorkletControlMessage);
 *   - из node.port значимо только фатальное { type: "worklet_error" }; прочие
 *     сообщения логируются.
 */

/** Имя процессора; зафиксировано registerProcessor в ворклете. */
const PROCESSOR_NAME = "noise-cancellation-processor";

/**
 * Размеры тайминг-модели движка в сэмплах @48кГц. Все значения кратны
 * workletFrameSize (128 — рендер-квант), иначе конструктор ворклета бросает
 * исключение. latency поднят относительно эталона потребителя из
 * engine-репозитория (DEFAULT_STABILITY_CONTROLLER_PARAMS: 512/128/384/512),
 * поэтому totalLatency = latency + lookahead + workletFrameSize = 1024 + 384 +
 * 128 = 1536 сэмплов (~32 мс @48кГц).
 */
const STABILITY_CONTROLLER_PARAMS = {
  denoiseFrameSize: 512,
  workletFrameSize: 128,
  lookahead: 384,
  latency: 1024,
};

/**
 * Режим выхода ворклета (WorkletMode в пакете ворклета). "denoise" — кадры
 * уходят движку, "bypass" — не уходят, а выход следует за сырым входом на том
 * же таймлайне; mute приоритетнее режима.
 */
type WorkletMode = "denoise" | "bypass";

/** Управляющие сообщения ворклету (канал «главный поток → ворклет»). */
type WorkletControlMessage =
  | { type: "loading_done" }
  | { type: "track_mute_change"; muted: boolean }
  | { type: "set_mode"; mode: WorkletMode }
  | { type: "destroy" };

/**
 * addModule кэшируется на контекст: повторная регистрация процессора под тем
 * же именем бросает NotSupportedError, а узел ворклета в проекте пересоздаётся
 * многократно (init и restart процессора, смена устройства, повторное поднятие
 * движка после его потери).
 */
const workletModules = new WeakMap<AudioContext, Promise<void>>();

function registerWorkletModule(
  context: AudioContext,
  workletUrl: string,
): Promise<void> {
  let registered = workletModules.get(context);
  if (!registered) {
    registered = context.audioWorklet.addModule(workletUrl).catch((error) => {
      // Неудачную регистрацию не кэшируем — повторная попытка должна работать.
      workletModules.delete(context);
      throw error;
    });
    workletModules.set(context, registered);
  }
  return registered;
}

/** Текст произвольной ошибки для сообщений. */
function errorText(value: unknown): string {
  if (value == null) return "неизвестная ошибка";
  if (value instanceof Error) return value.message || String(value);
  if (typeof value === "object" && "message" in value) {
    return String(value.message) || String(value);
  }
  return String(value);
}

export interface NoiseCancellation {
  /** Узел ворклета, моно 1-вход/1-выход; врезается между источником и приёмником. */
  readonly node: AudioWorkletNode;
  /**
   * Резолвится, когда движок готов (или сразу, если движка нет); отклоняется
   * при сбое инициализации движка и при разрушении пайплайна до готовности
   * (destroy() или фатал).
   */
  readonly ready: Promise<void>;
  setMuted(muted: boolean): void;
  /**
   * Режим выхода узла: "bypass" перестаёт отправлять кадры движку и отдаёт
   * сырой вход на том же таймлайне, "denoise" возвращает аудио движку. Работает
   * и до готовности движка.
   */
  setMode(mode: "denoise" | "bypass"): void;
  destroy(): void;
}

export interface NoiseCancellationOptions {
  context: AudioContext;
  workletUrl: string;
  /**
   * URL движка. Без него воркер не создаётся: узел остаётся passthrough
   * навсегда, `ready` разрешён сразу, работает только mute.
   */
  workerUrl?: string;
  /** Фатальный сбой ПОСЛЕ готовности (worklet_error или ошибка воркера). */
  onError?: (error: unknown) => void;
}

/**
 * Контроллер пайплайна: владеет узлом и опциональным воркером движка, выполняет
 * хендшейк портов, ретранслирует готовность, раздаёт фатальные события и умеет
 * mute/destroy.
 */
class NoiseCancellationController implements NoiseCancellation {
  readonly node: AudioWorkletNode;
  readonly ready: Promise<void>;

  private worker: Worker | null;
  private readonly onError: ((error: unknown) => void) | undefined;

  /** Движок готов (loading_done доставлен ворклету). */
  private readyState = false;
  /** destroy()/фатальный сбой: всё отключено, методы — no-op. */
  private disposed = false;
  private mutedState = false;
  /** Режим выхода узла; узел создаётся в "denoise" — initialMode не передаём. */
  private modeState: WorkletMode = "denoise";

  private resolveReady!: () => void;
  private rejectReady!: (error: unknown) => void;

  constructor(
    node: AudioWorkletNode,
    worker: Worker | null,
    onError: ((error: unknown) => void) | undefined,
  ) {
    this.node = node;
    this.worker = worker;
    this.onError = onError;
    this.ready = new Promise<void>((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });
    // Разрушение пайплайна до готовности отклоняет `ready` (см. dispose), а
    // ждать его в этот момент могут уже не все: отказ не должен всплывать как
    // unhandled rejection. Потребительский `await this.ready` при этом
    // по-прежнему получает отказ — эта ветка его не подменяет.
    void this.ready.catch(() => {});

    // Движка нет (workerUrl не передан): ждать нечего — passthrough-узел готов
    // сразу, а `ready` нельзя оставлять неразрешённым (его ждёт вызывающий).
    if (!worker) {
      this.readyState = true;
      this.resolveReady();
    }

    // Канал ворклета: только фатальные события.
    node.port.onmessage = (event: MessageEvent): void => {
      if (this.disposed) return;
      const data: unknown = event.data;
      if (typeof data === "object" && data !== null && "type" in data) {
        if (data.type === "worklet_error") {
          const detail =
            "error" in data ? errorText(data.error) : "неизвестная ошибка";
          this.fail(`фатальное событие ворклета: ${detail}`, data);
          return;
        }
      }
      console.warn("[noise-cancellation] незнакомое сообщение node.port:", data);
    };

    if (!worker) return;

    // Канал воркера: хендшейк порта и объявления движка.
    worker.onmessage = (event: MessageEvent): void => {
      if (this.disposed) return;
      const data: unknown = event.data;

      // Первое сообщение — порт ворклета; отдаём его первым сообщением узла.
      if (data instanceof MessagePort) {
        this.node.port.postMessage(data, [data]);
        return;
      }

      if (typeof data === "object" && data !== null && "type" in data) {
        if (data.type === "loading_done") {
          const message: WorkletControlMessage = { type: "loading_done" };
          this.node.port.postMessage(message);
          this.readyState = true;
          console.info("[noise-cancellation] движок готов");
          this.resolveReady();
          return;
        }
        if (data.type === "statistics") {
          return;
        }
      }

      // Всё прочее — сырое значение канала движка (при провале инициализации
      // оно приходит вместо loading_done).
      this.fail(`ошибка engine-воркера: ${errorText(data)}`, data);
    };

    worker.onerror = (event: ErrorEvent): void => {
      if (this.disposed) return;
      this.fail(
        `ошибка engine-воркера: ${event.message || "смотрите консоль"}`,
        event,
      );
    };
  }

  setMuted(muted: boolean): void {
    if (this.disposed || muted === this.mutedState) return;
    this.mutedState = muted;
    // Работает и до готовности: ворклет запоминает флаг и молчит всю загрузку.
    const message: WorkletControlMessage = { type: "track_mute_change", muted };
    this.node.port.postMessage(message);
  }

  setMode(mode: WorkletMode): void {
    if (this.disposed || mode === this.modeState) return;
    this.modeState = mode;
    // Работает и до готовности движка: пока идёт загрузка, узел всё равно
    // отдаёт сырой вход, а режим применится к уже готовому пайплайну.
    const message: WorkletControlMessage = { type: "set_mode", mode };
    this.node.port.postMessage(message);
  }

  destroy(): void {
    this.dispose();
  }

  /**
   * Фатальный сбой: пайплайн разрушается здесь же. До готовности `ready`
   * отклоняется, после готовности событие уходит в onError.
   *
   * Порядок веток важен: до готовности `ready` отклоняется ЗДЕСЬ и первым, со
   * специфичной причиной, а dispose() идёт следом (его общий отказ тогда уже
   * no-op) — иначе причина сбоя потерялась бы.
   */
  private fail(detail: string, value: unknown): void {
    if (!this.readyState) {
      this.rejectReady(new Error(`[noise-cancellation] ${detail}`));
      this.dispose();
      return;
    }
    this.dispose();
    console.error(`[noise-cancellation] ${detail}`);
    this.onError?.(value);
  }

  /**
   * Полное разрушение пайплайна: destroy ворклету → снятие обработчика узла →
   * disconnect узла → terminate воркера. Идемпотентно; колбэки не вызывает.
   *
   * Незавершённый `ready` отклоняется: иначе ожидание готовности у вызывающего
   * (гонка в AudioProcessor.mountNoiseWorklet) не закончилось бы никогда —
   * пайплайн разрушен, события от него уже не придут. Отказом, а не
   * разрешением: готовности не было, и выдавать её за наступившую нельзя.
   */
  private dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    // Повторный settle — no-op, поэтому уже разрешённый `ready` (движок успел
    // подняться) и уже отклонённый `fail()`-ом здесь не портятся.
    this.rejectReady(
      new Error("[noise-cancellation] пайплайн разрушен до готовности движка"),
    );

    try {
      const message: WorkletControlMessage = { type: "destroy" };
      this.node.port.postMessage(message);
    } catch (error) {
      console.warn("[noise-cancellation] destroy:", error);
    }
    this.node.port.onmessage = null;

    try {
      this.node.disconnect();
    } catch {
      // Узел мог быть ни разу не подключён — disconnect тогда штатно бросает.
    }

    if (this.worker) {
      this.worker.onmessage = null;
      this.worker.onerror = null;
      this.worker.terminate();
      this.worker = null;
    }
  }
}

/**
 * Поднять пайплайн шумоподавления: зарегистрировать ворклет в AudioContext,
 * создать узел и — если передан `workerUrl` — engine-воркер с хендшейком.
 * Возвращается сразу после создания ресурсов; готовность движка приходит
 * отдельно через `ready`.
 *
 * Без `workerUrl` воркер не создаётся вообще: узел навсегда passthrough, а
 * `ready` уже разрешён — вызывающий не платит ожиданием загрузки модели.
 *
 * Отклоняется, если не загрузился модуль ворклета, не создался узел или
 * воркер; в этом случае в аудиограф ещё ничего не врезано.
 */
export async function createNoiseCancellation(
  options: NoiseCancellationOptions,
): Promise<NoiseCancellation> {
  const { context, workletUrl, workerUrl, onError } = options;

  await registerWorkletModule(context, workletUrl);

  const node = new AudioWorkletNode(context, PROCESSOR_NAME, {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [1],
    channelCount: 1,
    channelCountMode: "explicit",
    // stabilityControllerParams обязателен: конструктор ворклета читает поле
    // напрямую и бросает TypeError, если его нет.
    processorOptions: { stabilityControllerParams: STABILITY_CONTROLLER_PARAMS },
  });

  if (!workerUrl) {
    return new NoiseCancellationController(node, null, onError);
  }

  // Воркер создаётся и подписывается в одном синхронном шаге (в конструкторе
  // контроллера): сообщение движка не может прийти раньше обработчиков.
  let worker: Worker;
  try {
    worker = new Worker(workerUrl, { type: "classic" });
  } catch (error) {
    try {
      node.disconnect();
    } catch {
      // Узел ещё никуда не подключён — гасим штатное исключение.
    }
    throw error;
  }

  return new NoiseCancellationController(node, worker, onError);
}
